<template>
  <div ref="containerRef" :style="wrapperStyle">
    <slot />
    <div v-show="watermark" :style="watermarkStyle" />
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, shallowRef, watch } from 'vue'
import { debounce } from 'lodash-unified'
import { useWatermark } from '@element-plus/hooks'
import { watermarkV2Props } from './watermark'
import { getPixelRatio } from './utils'
import type {
  WatermarkV2CfgPartial,
  WatermarkV2FontPartial,
} from '@element-plus/hooks'
import type { CSSProperties } from 'vue'

const WatermarkFactory = useWatermark()

defineOptions({
  name: 'ElWatermarkV2',
})

const props = defineProps(watermarkV2Props)

const containerRef = shallowRef<HTMLDivElement | null>(null)
const watermark = ref('')
const watermarkWidth = ref(0)

const watermarkCfg = computed<WatermarkV2CfgPartial>(() => {
  const {
    image,
    content = WatermarkFactory.defaultConfig.content,
    width,
    height,
    fillStyle,
    textAlign,
    textBaseline,
    font,
    globalAlpha,
    rotate,
    gap,
  } = props

  if (image) {
    return {
      image,
      width,
      height,
      globalAlpha,
      rotate,
      gap,
    }
  }
  return {
    content,
    width,
    height,
    fillStyle,
    textAlign,
    textBaseline,
    font: font as string | WatermarkV2FontPartial,
    globalAlpha,
    rotate,
    gap,
  }
})

const wrapperStyle = computed<CSSProperties>(() => {
  return {
    position: 'relative',
  }
})

let drawWatermarkDebounce: undefined | ReturnType<typeof debounce> = undefined

const gapX = computed(() =>
  Array.isArray(props.gap) ? props.gap[0] : props.gap
)
const gapY = computed(() =>
  Array.isArray(props.gap) ? props.gap[1] || props.gap[0] : props.gap
)
const gapXCenter = computed(() => gapX.value / 2)
const gapYCenter = computed(() => gapY.value / 2)
const offsetLeft = computed(() =>
  Array.isArray(props.offset)
    ? props.offset[0]
    : props.offset ?? gapXCenter.value
)
const offsetTop = computed(() =>
  Array.isArray(props.offset)
    ? props.offset[1] || props.offset[0]
    : props.offset ?? gapYCenter.value
)

const watermarkStyle = computed<CSSProperties>(() => {
  const defaultStyle: CSSProperties = {
    zIndex: props.zIndex,
    position: 'absolute',
    left: 0,
    top: 0,
    width: '100%',
    height: '100%',
    pointerEvents: 'none',
    backgroundRepeat: props.repeat,
  }

  // 偏移
  let positionLeft = offsetLeft.value - gapXCenter.value
  let positionTop = offsetTop.value - gapYCenter.value
  if (positionLeft > 0) {
    defaultStyle.left = `${positionLeft}px`
    defaultStyle.width = `calc(100% - ${positionLeft}px)`
    positionLeft = 0
  }
  if (positionTop > 0) {
    defaultStyle.top = `${positionTop}px`
    defaultStyle.height = `calc(100% - ${positionTop}px)`
    positionTop = 0
  }
  defaultStyle.backgroundPosition = `${positionLeft}px ${positionTop}px`

  if (watermark.value) {
    defaultStyle.backgroundImage = `url(${watermark.value})`
  }

  if (watermarkWidth.value) {
    defaultStyle.backgroundSize = `${Math.floor(watermarkWidth.value)}px`
  }

  return defaultStyle
})

watch(
  watermarkCfg,
  () => {
    drawWatermarkDebounce && drawWatermarkDebounce()
  },
  {
    deep: true,
  }
)

function drawWatermark(): void {
  if (watermarkCfg.value.image) {
    watermarkWidth.value = 0
    WatermarkFactory.createImageWatermarkTexture(watermarkCfg.value)
      .then((texture) => {
        if (texture) {
          watermark.value = texture.toDataURL('image/png')
        } else {
          watermark.value = ''
        }
      })
      .catch(() => {
        watermark.value = ''
      })
  } else {
    const ratio = getPixelRatio()
    const texture = WatermarkFactory.createWatermarkTexture(
      watermarkCfg.value,
      ratio
    )
    if (texture && texture.toDataURL) {
      watermark.value = texture.toDataURL('image/png')
      watermarkWidth.value = texture.width / ratio
    } else {
      watermark.value = ''
      watermarkWidth.value = 0
    }
  }
}

onMounted(() => {
  drawWatermark()
  drawWatermarkDebounce = debounce(drawWatermark, props.debounce)
})
</script>
